import { useEffect, useMemo, useState } from 'react'
import { Group, Skeleton, Stack, Text } from '@mantine/core'
import { ContactCard } from './ContactCard'
import { RequestNewParticipantLink } from './RequestNewParticipantLink'
import { Button } from '../Common/Button'
import { StatusMessage } from '../Common/StatusMessage'
import { isComposeMode } from '../../utils/officeHelpers'
import type { Contact } from '../../types/contact'

/* global Office */

export interface ContactsTabProps {
  // The connected claim — unmatched recipients are saved to it as ad-hoc contacts.
  claimNumber: string
  participants: Contact[]
  adhocContacts: Contact[]
  loading?: boolean
  error?: string | null
  // Retries the claim contacts fetch (participants + ad-hoc) when it failed.
  onRetry?: () => void
}

interface EmailRecipient {
  name: string
  email: string
}

// Manual To/Cc edits are picked up by polling at this cadence. Polling is used on
// every host instead of the RecipientsChanged event, which is unreliable across
// Outlook versions (new Outlook web registers it but never fires it).
const RECIPIENTS_POLL_MS = 3000

// How long the "Contact added to claim." confirmation stays visible (MSD-257).
const SAVE_SUCCESS_MS = 4000

// ─── Office.js recipient reading (inline to this tab) ──────────────────────────

function readRecipientField(field: Office.Recipients): Promise<EmailRecipient[]> {
  return new Promise((resolve, reject) => {
    field.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        // Unresolved entries (e.g. contact groups) can come back without an email address.
        resolve(result.value.map((r) => ({ name: r.displayName ?? '', email: r.emailAddress ?? '' })))
      } else {
        reject(new Error(result.error?.message || 'Failed to read recipients'))
      }
    })
  })
}

async function readEmailRecipients(): Promise<EmailRecipient[]> {
  const item = Office?.context?.mailbox?.item
  if (!item) return []

  // Compose: surface To + Cc. Read: surface the sender (From) only.
  if (isComposeMode()) {
    const compose = item as Office.MessageCompose
    const [to, cc] = await Promise.all([readRecipientField(compose.to), readRecipientField(compose.cc)])
    return [...to, ...cc]
  }

  const from = (item as Office.MessageRead).from
  if (!from) return []
  return [{ name: from.displayName, email: from.emailAddress }]
}

// ─── List building (no nested ternaries — Sonar S3358) ─────────────────────────

function sortByRoleThenName(contacts: Contact[]): Contact[] {
  return [...contacts].sort((a, b) => {
    const roleCompare = (a.role ?? '').localeCompare(b.role ?? '')
    if (roleCompare !== 0) return roleCompare
    return (a.name ?? '').localeCompare(b.name ?? '')
  })
}

// Every email comparison in this tab goes through here. MC1 pads its string fields, and
// padding is invisible on screen but compares unequal: it split one person into two cards
// and stopped a To-line participant being promoted to the top (MSD-642). The service
// trims on the way in, and normalising again at each comparison means no future source of
// padded data can bring that back.
function emailKey(email: string | null | undefined): string {
  return email?.trim().toLowerCase() ?? ''
}

// Email recipients that don't match a known claim participant, deduped by email.
function buildAdHocContacts(recipients: EmailRecipient[], participants: Contact[]): Contact[] {
  const known = new Set(participants.map((p) => emailKey(p.email)).filter(Boolean))
  const seen = new Set<string>()
  const result: Contact[] = []
  for (const recipient of recipients) {
    const key = emailKey(recipient.email)
    if (!key || known.has(key) || seen.has(key)) continue
    seen.add(key)
    // Outlook returns displayName === emailAddress for addresses it can't resolve to a
    // contact, so treat that (and an empty name) as "no name" — leave the name blank
    // rather than showing the email or a placeholder.
    const hasRealName = recipient.name.trim() !== '' && recipient.name.trim().toLowerCase() !== key
    result.push({
      id: `unmatched-${recipient.email}`,
      email: recipient.email,
      name: hasRealName ? recipient.name : '',
      isAdHoc: true,
      isUnmatched: true,
    })
  }
  return result
}

// MC1 returns one participant row per party-role pair, and the same pair can come back more
// than once — one UAT claim listed Vince Sherlock as Lead Adjuster twice, identical in every
// field. A row repeating the name, email and role is the same record arriving twice, so only
// the first is kept (MSD-642).
//
// Rows that differ in role are deliberately left alone: someone holding several roles on a
// claim is several entries by design, and QA confirmed on 17 Aug that collapsing them into a
// single contact is not wanted. A row carrying neither a name nor an email is kept as well —
// there is nothing there to identify it by.
function dropRepeatedParticipants(participants: Contact[]): Contact[] {
  const seen = new Set<string>()
  return participants.filter((participant) => {
    const name = participant.name?.trim().toLowerCase() ?? ''
    const email = emailKey(participant.email)
    if (!name && !email) return true

    const key = `${name} <> ${email} <> ${participant.role?.trim().toLowerCase() ?? ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// One flat list: everyone already on the open email first — participants then persisted
// ad-hoc contacts — followed by the claim's remaining participants and remaining ad-hoc
// contacts, and finally any email recipients that aren't on the claim at all.
//
// Ad-hoc contacts are promoted for being on the email just as participants are (MSD-642):
// the story asks for recipients from the To field at the top of the tab, and an ad-hoc
// contact left in backend order sat below every participant even with its address on the
// To line. Unmatched recipients stay last on purpose — they are not on the claim yet, and
// their card is a prompt to add them rather than a contact to reach for.
function buildContacts(recipients: EmailRecipient[], participants: Contact[], adhocContacts: Contact[]): Contact[] {
  const recipientEmails = new Set(recipients.map((r) => emailKey(r.email)).filter(Boolean))
  const inEmail = (p: Contact) => {
    const key = emailKey(p.email)
    return Boolean(key) && recipientEmails.has(key)
  }
  const people = dropRepeatedParticipants(participants)
  const matched = people.filter((p) => inEmail(p))
  const rest = people.filter((p) => !inEmail(p))
  // A person saved as ad-hoc may since have been added as an MC1 participant, and the
  // ad-hoc list can also repeat the same person: rows saved before the backend began
  // rejecting repeated name+email pairs are still on older claims, and two rows sharing
  // an email would collide on one React key as well. Keep the first of each (MSD-642).
  const participantEmails = new Set(people.map((p) => emailKey(p.email)).filter(Boolean))
  const seenAdhoc = new Set<string>()
  const uniqueAdhoc = adhocContacts.filter((a) => {
    const key = emailKey(a.email)
    // An ad-hoc contact with no email can't be matched against anything — keep it.
    if (!key) return true
    if (participantEmails.has(key) || seenAdhoc.has(key)) return false
    seenAdhoc.add(key)
    return true
  })
  // Backend order is kept within each group: ad-hoc contacts have no meaningful role to
  // sort on (the proc returns 'Unknown'), so sorting them would only shuffle the list.
  const matchedAdhoc = uniqueAdhoc.filter((a) => inEmail(a))
  const restAdhoc = uniqueAdhoc.filter((a) => !inEmail(a))
  const emailAdHoc = buildAdHocContacts(recipients, [...people, ...adhocContacts])
  return [...sortByRoleThenName(matched), ...matchedAdhoc, ...sortByRoleThenName(rest), ...restAdhoc, ...emailAdHoc]
}

// Identifies an exact name+email repeat — the pair the proc rejects (Msg 105). Trimmed and
// lower-cased to match its comparison; the separator can't occur in an email address, so no
// two distinct pairs collide.
function contactPairKey(name: string, email: string): string {
  return `${name.trim().toLowerCase()} <> ${email.trim().toLowerCase()}`
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function ContactsTab({
  claimNumber,
  participants,
  adhocContacts,
  loading: contactsLoading,
  error: contactsError,
  onRetry,
}: Readonly<ContactsTabProps>) {
  const [recipients, setRecipients] = useState<EmailRecipient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  // Transient confirmation after an ad-hoc contact was saved (MSD-257).
  const [saveSuccess, setSaveSuccess] = useState(false)

  useEffect(() => {
    if (!saveSuccess) return
    const timeoutId = globalThis.setTimeout(() => setSaveSuccess(false), SAVE_SUCCESS_MS)
    return () => globalThis.clearTimeout(timeoutId)
  }, [saveSuccess])

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const result = await readEmailRecipients()
        if (cancelled) return
        setRecipients(result)
        setError(null)
      } catch (err) {
        console.error('[ContactsTab] Failed to read recipients:', err)
        if (!cancelled) setError('Failed to read email recipients.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    Promise.resolve().then(load)

    // Keep the list in sync with To/Cc edits made while the tab is open (compose
    // only) by polling. The RecipientsChanged event is deliberately not used: it is
    // not delivered on every host (new Outlook web registers it but never fires it),
    // so polling is the one path that behaves the same everywhere. Adds via our own
    // send button re-read immediately (onAdded).
    const pollId = isComposeMode() ? globalThis.setInterval(load, RECIPIENTS_POLL_MS) : undefined
    return () => {
      cancelled = true
      if (pollId !== undefined) globalThis.clearInterval(pollId)
    }
  }, [reloadKey])

  // A card's send button was clicked (or the address was already present) — re-read
  // the recipients now so the button disables without waiting for the next poll.
  const handleRecipientAdded = () => setReloadKey((k) => k + 1)

  const contacts = useMemo(
    () => buildContacts(recipients, participants, adhocContacts),
    [recipients, participants, adhocContacts]
  )

  // Emails currently on the email (To/Cc while composing). A contact whose email is
  // here has already been added, so its send button is disabled; removing the
  // recipient updates `recipients` (event or poll) and re-enables it.
  const recipientEmails = useMemo(() => new Set(recipients.map((r) => emailKey(r.email)).filter(Boolean)), [recipients])

  // Names, emails and name+email pairs already on the claim (participants + persisted
  // ad-hoc contacts), used to decide whether the entry form warns before saving.
  const claimContactKeys = useMemo(() => {
    const existing = [...participants, ...adhocContacts]
    return {
      emails: new Set(existing.map((c) => emailKey(c.email)).filter(Boolean)),
      names: new Set(existing.map((c) => c.name?.trim().toLowerCase()).filter(Boolean)),
      pairs: new Set(existing.map((c) => contactPairKey(c.name ?? '', c.email ?? ''))),
    }
  }, [participants, adhocContacts])

  // Each card saves its own unmatched recipient (its form owns the in-flight state);
  // on success we confirm and refetch so the saved contact reappears as a persisted
  // entry, replacing its unmatched card.
  const addToClaim = {
    claimNumber,
    // Warn only when part of the contact repeats an existing one — a shared name or a shared
    // mailbox is often legitimate, so the user confirms and the save goes ahead. An exact
    // name+email repeat deliberately skips the warning: the proc rejects that pair (Msg 105),
    // so asking first would only delay the answer the backend already gives.
    isPartialDuplicate: (name: string, email: string) => {
      if (claimContactKeys.pairs.has(contactPairKey(name, email))) return false
      return claimContactKeys.emails.has(emailKey(email)) || claimContactKeys.names.has(name.trim().toLowerCase())
    },
    onSaved: () => {
      setSaveSuccess(true)
      onRetry?.()
    },
  }

  const isLoading = loading || Boolean(contactsLoading)
  const displayError = error ?? contactsError ?? null
  const isEmpty = !isLoading && !displayError && contacts.length === 0
  // Skeletons are for the first load only. A refetch (after saving an ad-hoc contact,
  // or from the Refresh icon) keeps the cards mounted so open forms and their edits
  // survive — replacing them with skeletons reads as the tab reloading (MSD-255).
  const showSkeleton = loading || (isLoading && contacts.length === 0)
  // Recipients can only be added while composing — not when viewing an existing email.
  const canAddToEmail = isComposeMode()

  // Retries whichever side failed: the Office recipients read, the API fetch, or both.
  const handleRetry = () => {
    if (error) {
      setError(null)
      setLoading(true)
      setReloadKey((k) => k + 1)
    }
    if (contactsError) onRetry?.()
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="xs" c="dimmed">
          Recent Contacts ({contacts.length})
        </Text>
        <RequestNewParticipantLink />
      </Group>

      {saveSuccess && (
        <StatusMessage status="success" fz="xs">
          Contact added to claim.
        </StatusMessage>
      )}

      {showSkeleton && (
        <Stack gap={4}>
          <Skeleton height={56} radius="sm" />
          <Skeleton height={56} radius="sm" />
        </Stack>
      )}

      {displayError && (
        <Stack gap="xs">
          <StatusMessage status="error">{displayError}</StatusMessage>
          <Button label="Retry" size="xs" variant="outline" onClick={handleRetry} />
        </Stack>
      )}

      {isEmpty && (
        <Text fz="xs" c="dimmed">
          No participants on this claim yet.
        </Text>
      )}

      {!showSkeleton && contacts.length > 0 && (
        <Stack gap={4}>
          {contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              canAddToEmail={canAddToEmail}
              alreadyAdded={Boolean(emailKey(contact.email)) && recipientEmails.has(emailKey(contact.email))}
              onAdded={handleRecipientAdded}
              addToClaim={addToClaim}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
